// LLMId Mapper - Maps human-readable LLMIds to actual system IDs
// This helps the LLM work with simple, memorable IDs instead of complex system IDs

const STORAGE_KEY = 'llmIdMapper_mappings';

interface IdMapping {
  llmId: string;
  actualId: string;
  type: 'window' | 'document' | 'node';
  entityType?: string;
  name?: string;
  createdAt: number;
}

class LLMIdMapper {
  private mappings: Map<string, IdMapping> = new Map();
  private reverseMappings: Map<string, string> = new Map(); // actualId -> llmId

  constructor() {
    this._loadFromStorage();
  }

  private _loadFromStorage(): void {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const entries: IdMapping[] = JSON.parse(raw);
      for (const m of entries) {
        this.mappings.set(m.llmId, m);
        if (!m.actualId.startsWith('pending-')) {
          this.reverseMappings.set(m.actualId, m.llmId);
        }
      }
    } catch {
      // Storage unavailable or corrupted — start fresh
    }
  }

  private _saveToStorage(): void {
    try {
      const entries = Array.from(this.mappings.values());
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
      // sessionStorage full or unavailable — proceed without persistence
    }
  }

  /**
   * Pre-register an LLMId before entity creation to prevent 404s during generation
   */
  preRegisterLlmId(llmId: string, entityType: string, name: string): void {
    const mapping: IdMapping = {
      llmId,
      actualId: 'pending-' + llmId, // Temporary placeholder
      type: 'document',
      entityType,
      name,
      createdAt: Date.now()
    };

    this.mappings.set(llmId, mapping);
    // Don't add to reverseMappings until we have the actual ID
    this._saveToStorage();
  }

  /**
   * Register a mapping between LLMId and actual system ID
   */
  registerMapping(llmId: string, actualId: string, type: 'window' | 'document' | 'node', entityType?: string, name?: string): void {
    const mapping: IdMapping = {
      llmId,
      actualId,
      type,
      entityType,
      name,
      createdAt: Date.now()
    };

    this.mappings.set(llmId, mapping);
    this.reverseMappings.set(actualId, llmId);
    this._saveToStorage();
  }

  /**
   * Get actual system ID from LLMId
   */
  getActualId(llmId: string): string | null {
    const mapping = this.mappings.get(llmId);
    return mapping ? mapping.actualId : null;
  }

  /**
   * Get LLMId from actual system ID
   */
  getLLMId(actualId: string): string | null {
    return this.reverseMappings.get(actualId) || null;
  }

  /**
   * Get mapping details
   */
  getMapping(llmId: string): IdMapping | null {
    return this.mappings.get(llmId) || null;
  }

  /**
   * Check if LLMId exists
   */
  hasLLMId(llmId: string): boolean {
    return this.mappings.has(llmId);
  }

  /**
   * Get all mappings of a specific type
   */
  getMappingsByType(type: 'window' | 'document' | 'node'): IdMapping[] {
    return Array.from(this.mappings.values()).filter(m => m.type === type);
  }

  /**
   * Get all mappings for a specific entity type
   */
  getMappingsByEntityType(entityType: string): IdMapping[] {
    return Array.from(this.mappings.values()).filter(m => m.entityType === entityType);
  }

  /**
   * Remove a mapping
   */
  removeMapping(llmId: string): boolean {
    const mapping = this.mappings.get(llmId);
    if (mapping) {
      this.mappings.delete(llmId);
      this.reverseMappings.delete(mapping.actualId);
      this._saveToStorage();
      return true;
    }
    return false;
  }

  /**
   * Clear all mappings
   */
  clear(): void {
    this.mappings.clear();
    this.reverseMappings.clear();
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }

  /**
   * Get all mappings as a list (for debugging)
   */
  getAllMappings(): IdMapping[] {
    return Array.from(this.mappings.values());
  }

  /**
   * Generate a summary of available mappings for LLM context
   */
  getContextSummary(): string {
    const windows = this.getMappingsByType('window');
    const documents = this.getMappingsByType('document');
    const nodes = this.getMappingsByType('node');

    let summary = '📋 Available ID Mappings:\n';
    
    if (windows.length > 0) {
      summary += '\n🪟 Windows:\n';
      windows.forEach(w => {
        summary += `  ${w.llmId} → ${w.name || w.actualId}\n`;
      });
    }

    if (documents.length > 0) {
      summary += '\n📄 Documents:\n';
      documents.forEach(d => {
        summary += `  ${d.llmId} → ${d.name || d.actualId}\n`;
      });
    }

    if (nodes.length > 0) {
      summary += '\n🔗 Nodes:\n';
      nodes.slice(0, 10).forEach(n => { // Limit to first 10 nodes
        summary += `  ${n.llmId} → ${n.name || n.actualId}\n`;
      });
      if (nodes.length > 10) {
        summary += `  ... and ${nodes.length - 10} more nodes\n`;
      }
    }

    return summary;
  }
}

// Export singleton instance
export const llmIdMapper = new LLMIdMapper();

// Helper functions for common operations
export const mapWindowId = (actualWindowId: string, windowName?: string): string => {
  const existingLLMId = llmIdMapper.getLLMId(actualWindowId);
  if (existingLLMId) {
    return existingLLMId;
  }

  // Generate new LLMId for window using simple word-based pattern
  const llmId = generateSimpleWindowId();
  
  llmIdMapper.registerMapping(llmId, actualWindowId, 'window', 'Window', windowName);
  return llmId;
};

// Simple word-based ID generation for windows
function generateSimpleWindowId(): string {
  const words = [
    'apple', 'banana', 'cherry', 'dragon', 'eagle', 'forest', 'garden', 'house',
    'island', 'jungle', 'knight', 'lemon', 'mountain', 'ocean', 'planet', 'queen',
    'river', 'star', 'tiger', 'umbrella', 'village', 'water', 'yellow', 'zebra',
    'anchor', 'bridge', 'castle', 'diamond', 'elephant', 'flower', 'guitar',
    'hammer', 'iceberg', 'jacket', 'kangaroo', 'lighthouse', 'monkey', 'notebook',
    'orange', 'penguin', 'quilt', 'rainbow', 'sunset', 'turtle', 'violin', 'window'
  ];
  
  // Select 2 random words for windows (shorter IDs)
  const word1 = words[Math.floor(Math.random() * words.length)];
  const word2 = words[Math.floor(Math.random() * words.length)];
  
  return `window_${word1}_${word2}`;
}

export const mapDocumentId = (actualDocId: string, docName?: string): string => {
  const existingLLMId = llmIdMapper.getLLMId(actualDocId);
  if (existingLLMId) {
    return existingLLMId;
  }

  // Generate new LLMId for document using the same word-based pattern as your entity generator
  const llmId = generateSimpleDocId();
  llmIdMapper.registerMapping(llmId, actualDocId, 'document', 'Doc', docName);
  return llmId;
};

// Simple word-based ID generation for documents (same style as entity generator)
function generateSimpleDocId(): string {
  const words = [
    'apple', 'banana', 'cherry', 'dragon', 'eagle', 'forest', 'garden', 'house',
    'island', 'jungle', 'knight', 'lemon', 'mountain', 'ocean', 'planet', 'queen',
    'river', 'star', 'tiger', 'umbrella', 'village', 'water', 'yellow', 'zebra',
    'anchor', 'bridge', 'castle', 'diamond', 'elephant', 'flower', 'guitar',
    'hammer', 'iceberg', 'jacket', 'kangaroo', 'lighthouse', 'monkey', 'notebook',
    'orange', 'penguin', 'quilt', 'rainbow', 'sunset', 'turtle', 'violin', 'window'
  ];
  
  // Select 3 random words for documents (same as entity generator)
  const word1 = words[Math.floor(Math.random() * words.length)];
  const word2 = words[Math.floor(Math.random() * words.length)];
  const word3 = words[Math.floor(Math.random() * words.length)];
  
  return `doc_${word1}_${word2}_${word3}`;
}

export const mapNodeId = (actualNodeId: string, nodeName?: string): string => {
  const existingLLMId = llmIdMapper.getLLMId(actualNodeId);
  if (existingLLMId) {
    return existingLLMId;
  }

  // Generate new LLMId for node using the same pattern as entities
  // Use simple word combinations that are easy for LLM to remember and replicate
  const llmId = generateSimpleNodeId();
  llmIdMapper.registerMapping(llmId, actualNodeId, 'node', 'Node', nodeName);
  return llmId;
};

// Simple word-based ID generation for nodes (same style as entity generator)
function generateSimpleNodeId(): string {
  const words = [
    'apple', 'banana', 'cherry', 'dragon', 'eagle', 'forest', 'garden', 'house',
    'island', 'jungle', 'knight', 'lemon', 'mountain', 'ocean', 'planet', 'queen',
    'river', 'star', 'tiger', 'umbrella', 'village', 'water', 'yellow', 'zebra',
    'anchor', 'bridge', 'castle', 'diamond', 'elephant', 'flower', 'guitar',
    'hammer', 'iceberg', 'jacket', 'kangaroo', 'lighthouse', 'monkey', 'notebook',
    'orange', 'penguin', 'quilt', 'rainbow', 'sunset', 'turtle', 'violin', 'window'
  ];
  
  // Select 2 random words for nodes (shorter than document IDs)
  const word1 = words[Math.floor(Math.random() * words.length)];
  const word2 = words[Math.floor(Math.random() * words.length)];
  
  return `node_${word1}_${word2}`;
}

// Utility to convert LLMId back to actual ID
export const getActualId = (llmId: string): string => {
  const actualId = llmIdMapper.getActualId(llmId);
  if (!actualId) {
    throw new Error(`LLMId not found: ${llmId}`);
  }
  return actualId;
};

// Utility to check if an ID is an LLMId
export const isLLMId = (id: string): boolean => {
  return llmIdMapper.hasLLMId(id);
};

// Utility to resolve any ID (LLMId or actual) to the actual system ID
export const resolveToActualId = (id: string): string => {
  // Check if it's an LLMId first
  const actualId = llmIdMapper.getActualId(id);
  if (actualId) {
    return actualId;
  }
  
  // If not found in mapping, assume it's already an actual ID
  return id;
};

// Utility to register entity mapping when entities are loaded
export const registerEntityMapping = (entity: any): void => {
  if (entity.llmId && entity._id) {
    llmIdMapper.registerMapping(entity.llmId, entity._id, 'document', entity.entityType, entity.name);
  }
}; 